import { useApolloClient } from "@apollo/client/react";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GetRecordComposerDefinitionDetailDocument, type GetRecordComposerDefinitionDetailQuery } from "~/common/gqls";
import { Button, Input, Label } from "~/components/ui";
import { useSetRecordUpdateOverrides } from "~/features/dataImportProjects/hooks/useSetRecordUpdateOverrides";
import {
  formToInput,
  type OverrideFormModel,
  type OverrideProperty,
  storedToForm,
  zRecordUpdateComposerDefinition,
} from "~/features/dataImportProjects/utils/recordUpdateOverride";
import { useTenant } from "~/features/tenant/context";

type Kind = "entity" | "relation";

// Serialize the editable rows for change detection against a freshly refetched form.
const rowsKey = (rows: OverrideProperty[]) => JSON.stringify(rows.map((r) => [r.key, r.template]));

export function RecordUpdateOverrideEditForm({
  recordUpdateComposerDefinitionId,
  initialForm,
  onSaved,
}: {
  recordUpdateComposerDefinitionId: string;
  initialForm: OverrideFormModel;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const client = useApolloClient();
  const { setOverrides, saving } = useSetRecordUpdateOverrides();

  const [entity, setEntity] = useState<OverrideProperty[]>(initialForm.entity);
  const [relation, setRelation] = useState<OverrideProperty[]>(initialForm.relation);

  const rowsFor = (kind: Kind) => (kind === "entity" ? entity : relation);
  const setRowsFor = (kind: Kind, rows: OverrideProperty[]) => (kind === "entity" ? setEntity : setRelation)(rows);

  const addRow = (kind: Kind) => setRowsFor(kind, [...rowsFor(kind), { key: "", template: "" }]);
  const removeRow = (kind: Kind, i: number) =>
    setRowsFor(
      kind,
      rowsFor(kind).filter((_, idx) => idx !== i),
    );
  const editRow = (kind: Kind, i: number, patch: Partial<OverrideProperty>) =>
    setRowsFor(
      kind,
      rowsFor(kind).map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );

  const hasEmptyKey = [...entity, ...relation].some((r) => r.key.trim() === "");
  const hasDuplicateKey = (rows: OverrideProperty[]) => {
    const keys = rows.map((r) => r.key.trim()).filter(Boolean);
    return new Set(keys).size !== keys.length;
  };
  const invalid = hasEmptyKey || hasDuplicateKey(entity) || hasDuplicateKey(relation);

  const onSubmit = async () => {
    // Refetch-before-submit: refuse to clobber a definition that changed since load.
    const { data } = await client.query<GetRecordComposerDefinitionDetailQuery>({
      query: GetRecordComposerDefinitionDetailDocument,
      variables: { tenantId, id: recordUpdateComposerDefinitionId },
      fetchPolicy: "network-only",
    });
    const current = data?.recordComposerDefinitions[0];
    const parsed = zRecordUpdateComposerDefinition.safeParse(current?.definition);
    const latest = storedToForm(parsed.success ? parsed.data.overrides : undefined);
    const changedUnderneath =
      !latest.editable ||
      rowsKey(latest.entity) !== rowsKey(initialForm.entity) ||
      rowsKey(latest.relation) !== rowsKey(initialForm.relation) ||
      JSON.stringify(latest.preservedScoped) !== JSON.stringify(initialForm.preservedScoped);
    if (changedUnderneath) {
      toast.error(t("feature.dataImportProjects.recordUpdateOverride.staleReload"));
      return;
    }

    const input = formToInput({ ...initialForm, editable: true, entity, relation });
    const ok = await setOverrides(recordUpdateComposerDefinitionId, input);
    if (!ok) return;
    await client.refetchQueries({ include: [GetRecordComposerDefinitionDetailDocument] });
    toast.success(t("feature.dataImportProjects.recordUpdateOverride.saved"));
    onSaved();
  };

  const renderRows = (kind: Kind) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{t(`feature.dataImportProjects.recordUpdateOverride.${kind}`)}</Label>
        <Button type="button" variant="ghost" size="sm" onClick={() => addRow(kind)}>
          <Plus className="size-3.5" />
          {t("feature.dataImportProjects.recordUpdateOverride.addRow")}
        </Button>
      </div>
      {rowsFor(kind).map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            className="flex-1"
            placeholder={t("feature.dataImportProjects.recordUpdateOverride.keyPlaceholder")}
            value={row.key}
            onChange={(e) => editRow(kind, i, { key: e.target.value })}
          />
          <Input
            className="flex-[2]"
            placeholder="{{properties.wikidataID}}"
            value={row.template}
            onChange={(e) => editRow(kind, i, { template: e.target.value })}
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(kind, i)}>
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {renderRows("entity")}
      {renderRows("relation")}
      <p className="text-12-regular text-fg-3">{t("feature.dataImportProjects.recordUpdateOverride.saveHint")}</p>
      <div className="flex justify-end">
        <Button variant="success" onClick={onSubmit} disabled={invalid || saving}>
          {saving ? t("ui.processing") : t("ui.save")}
        </Button>
      </div>
    </div>
  );
}
